import { SortModelItem } from 'ag-grid-enterprise'

import { getAgGridSortModelFromColumnState } from '../../components/Table/agGridSortPriority.utils'

export { getAgGridSortModelFromColumnState }

/**
 * Remove object keys not matching filter function.
 *
 * Defaults to removing undefined values.
 */
export const objectFilter = <T extends Record<string, any>>(
  obj: T,
  compare: (value: any, key: string) => boolean = value => value !== undefined,
) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => compare(value, key)),
  )

/**
 * Converts AG Grid sortModel to Electrum orderBy format
 * AG Grid format: [{ colId: 'fieldName', sort: 'asc' | 'desc' }]
 * Electrum format: [{ fieldName: 'ASC' | 'DESC' }]
 *
 * @param sortModel - AG Grid sort model
 */
export const agGridSortModelToOrderBy = (
  sortModel: SortModelItem[],
): Array<Record<string, 'ASC' | 'DESC'>> => {
  if (!sortModel || sortModel.length === 0) {
    return []
  }

  return sortModel
    .filter(
      (item): item is SortModelItem =>
        item !== null && item !== undefined && !!item.colId && !!item.sort,
    )
    .map(item => {
      const direction = item.sort === 'asc' ? 'ASC' : 'DESC'
      return {
        [item.colId]: direction,
      }
    })
}

/**
 * Converts Electrum orderBy format to AG Grid sortModel
 * Electrum format: [{ fieldName: 'ASC' | 'DESC' }]
 * AG Grid format: [{ colId: 'fieldName', sort: 'asc' | 'desc' }]
 *
 * @param orderBy - Electrum orderBy format
 */
export const orderByToAgGridSortModel = (
  orderBy: Array<Record<string, 'ASC' | 'DESC'>> | null | undefined,
): SortModelItem[] => {
  if (!orderBy || orderBy.length === 0) {
    return []
  }

  return orderBy
    .filter(item => item && typeof item === 'object')
    .map((item, index) => {
      const entries = Object.entries(item)
      if (entries.length === 0) {
        return null
      }

      const [fieldName, direction] = entries[0]
      const sort = direction === 'ASC' ? 'asc' : 'desc'

      return {
        colId: fieldName,
        sort,
        sortIndex: index,
      } as SortModelItem
    })
    .filter((item): item is SortModelItem => item !== null)
}
